// Public Patient-Prospect signup + intake service.
//
// Two execution modes, controlled by MOCK_PROSPECT_SIGNUP:
//
//   - **MOCK = true** (Phase 1 / local-dev): everything below the flag
//     stays in-process. Useful for UX iteration without a backend.
//
//   - **MOCK = false** (Phase 2e cutover, production): real flow uses
//     existing wellness OTP helpers (`sendWhatsAppOtp` /
//     `verifyWhatsAppOtp`) — these auto-create a `ROLE_USER` session and
//     write the auth token to localStorage, just like the standard
//     wellness sign-in. Once authenticated, we POST to
//     `/api/secure/me/patient-prospect/initialize` to add the prospect
//     role + create PatientProspectInfo + Lead/CareIntakeCase + run sync
//     triage. The standalone public endpoint
//     `/api/public/patient-prospect/signup` (Phase 2b) is also available
//     for callers that have a separate `otpToken` outside the
//     auto-registering WhatsApp path.
//
// Real endpoint contracts: docs/PATIENT_PROSPECT_SIGNUP_PLAN.md §6.

import axios from 'axios'
import {
  apiURL,
  commonHeadersWeb,
  getCountryCode,
  getMainLanguageCode,
  sendWhatsAppOtp as sendWhatsAppOtpRaw,
  verifyWhatsAppOtp as verifyWhatsAppOtpRaw,
} from 'src/consumer/services'
import {
  getAuthToken,
  isTokenExpired,
  setAccessTokenExpiry,
} from 'src/consumer/services/localstorageServices'
import { postData, getData } from 'src/consumer/services/profileService'
import { persistPortalAvailabilityOverridesFromAuthData } from 'src/shared/config/portalAvailabilityRules'

const PROSPECT_ME_BASE = '/api/secure/me/patient-prospect'
const ASSESSMENT_RESULTS_BASE = '/api/secure/me/assessment-results'

// SessionStorage key the AssessmentPlayer drops into when an anonymous
// visitor completes an assessment. /signup → /care/status reads it on
// mount, replays the result against the new authenticated endpoint, then
// clears it so a single completed take can't get double-counted.
//
// Why sessionStorage and not localStorage: this is a single-tab, single-
// session handoff — if the user closes the tab before signing up the
// take is rightfully lost (the legacy /api/secure/assessment/{id}/ans
// store still owns the wellness app's history).
export const PENDING_ASSESSMENT_RESULT_KEY = 'aumhumPendingAssessmentResult'

// Local-dev / Phase-1 toggle. When true, OTP + signup are simulated in
// the browser. When false, the real backend wiring above kicks in.
const MOCK_PROSPECT_SIGNUP = false

// Phase-1 mock session slot. Untouched in real mode — real auth uses the
// standard `localStorage.token` set by the wellness pipeline.
const MOCK_PROSPECT_TOKEN_KEY = 'mockPatientProspectSession'

function toErrorMessage(error, fallback) {
  const data = error?.response?.data
  return data?.message || data?.data?.cause || data?.cause || error?.message || fallback
}

/**
 * True when there's a non-expired auth token in localStorage from any
 * previous login (wellness signup, social login, etc).
 */
export function isLoggedIn() {
  try {
    return !!getAuthToken() && !isTokenExpired()
  } catch {
    return false
  }
}

/**
 * Best-effort read of the user's role list from localStorage.
 */
export function getStoredAuthorities() {
  try {
    const raw = window.localStorage.getItem('authorities')
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

// ---------------------------------------------------------------------------
// Mock helpers (Phase 1)
// ---------------------------------------------------------------------------

function delay(ms) {
  return new Promise((r) => setTimeout(r, ms))
}

function fakeId(prefix) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`
}

function fakeRecommendation(pathway, specialtyTag) {
  const bySpecialty = {
    psychiatry: 'A psychiatrist consultation looks like the right starting point. Our team will share 2–3 specialists who match.',
    psychology: 'Talk therapy with a clinical psychologist looks right for what you described. We will line up matches.',
    endocrinology: 'An endocrinologist evaluation is what we would suggest first. We will help you book one.',
    rheumatology: 'A rheumatology consult looks right. We will help you find a specialist that fits.',
    pediatrics: 'A pediatric consult is the right next step. Our coordinator will help line one up.',
    triage: 'A short call with our care coordinator will help us point you to the right specialist.',
  }
  return {
    primaryRecommendation: pathway,
    userFacingSummary: bySpecialty[specialtyTag] || bySpecialty.triage,
    rationale: 'Generated from your initial selection — refined as you share more.',
    generatedBy: 'RULE_ENGINE',
  }
}

function mockSignup(payload) {
  const session = {
    token: fakeId('mockTok'),
    userId: fakeId('user'),
    roles: ['ROLE_PATIENT_PROSPECT'],
    prospectInfoId: fakeId('prospect'),
    caseId: fakeId('case'),
    name: payload.name || null,
    mobile: payload.mobile,
    pathway: payload.pathway,
    specialtyTag: payload.specialtyTag,
    subjectType: payload.subjectType || 'SELF',
    createdAt: new Date().toISOString(),
    recommendation: fakeRecommendation(payload.pathway, payload.specialtyTag),
    contactOptions: {
      whatsapp: 'https://wa.me/918047492952',
      phone: '+918047492952',
      chatHandle: 'aumhum-care',
    },
  }
  try {
    window.localStorage.setItem(MOCK_PROSPECT_TOKEN_KEY, JSON.stringify(session))
  } catch {
    // ignore storage failure
  }
  return session
}

// ---------------------------------------------------------------------------
// Real-mode helpers
// ---------------------------------------------------------------------------

/**
 * Mirror of {@code handleAfterLoginCAMC}'s first 3 lines — write the
 * auth token + expiry + authorities into localStorage so the rest of the
 * app sees the user as logged in. Used after `verifyWhatsAppOtp`
 * succeeds, before we call the authenticated initialize endpoint.
 */
function persistAuthSession(authData) {
  if (!authData?.accessToken) return
  try {
    window.localStorage.setItem('token', authData.accessToken)
    setAccessTokenExpiry({ accessTokenExpiry: authData.accessTokenExpiry })
    if (Array.isArray(authData.authorities)) {
      window.localStorage.setItem('authorities', JSON.stringify(authData.authorities))
    }
    persistPortalAvailabilityOverridesFromAuthData(authData)
    // Notify auth-aware UI (e.g. AumhumSite TopBar) that the login state
    // changed in this tab — without this, components that read
    // localStorage at render time keep showing "Login" until the next
    // route change or page reload, even though the user IS signed in.
    if (typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent('aumhum:auth-changed'))
    }
  } catch (e) {
    // localStorage failures shouldn't block the flow; the user just won't
    // remain authenticated past this tab.
    console.warn('Failed to persist auth session:', e)
  }
}

/**
 * Real prospect signup for an *already authenticated* user. Calls the
 * Phase-2c authenticated POST /api/secure/me/patient-prospect/initialize.
 *
 * `postData` swallows axios errors and returns the AxiosError object
 * itself (which has `response.status` / `response.data`, NOT top-level
 * `status` / `data`), so we have to look at both shapes when deciding
 * success vs failure. The earlier version of this function only checked
 * `res.status`, which is undefined on an axios error — so a backend 400
 * fell through to a generic "could not start" message with no surface
 * for the actual server reason.
 */
async function initializeProspectAuthenticated(payload) {
  const res = await postData(PROSPECT_ME_BASE + '/initialize', payload)
  const httpStatus = res?.status ?? res?.response?.status
  const responseBody = res?.data ?? res?.response?.data
  if (httpStatus >= 200 && httpStatus < 300) {
    return responseBody || {}
  }
  const err = new Error(toErrorMessage(res, 'We could not start your care request just now.'))
  err.code = responseBody?.errorCode || httpStatus
  err.httpStatus = httpStatus
  throw err
}

// ---------------------------------------------------------------------------
// Public API used by the prospect signup flow
// ---------------------------------------------------------------------------

/**
 * Send an OTP to the mobile via WhatsApp (preferred). The frontend asks
 * for SMS only if the WhatsApp path fails or the user clicks the
 * fallback link after the resend timer; in real mode we route SMS through
 * Firebase the same way the existing wellness modal does (handled by the
 * caller — this function only owns the WhatsApp side, since SMS needs a
 * RecaptchaVerifier component).
 */
export async function sendProspectOtp(mobile, channel = 'whatsapp', options = {}) {
  if (MOCK_PROSPECT_SIGNUP) {
    await delay(700)
    return {
      sessionId: fakeId('otp'),
      channel,
      otpExpirySeconds: 300,
      resendDelaySeconds: 20,
    }
  }
  if (channel !== 'whatsapp') {
    throw new Error('SMS code option is coming soon. Please use WhatsApp for now.')
  }
  // Forward the optional reCAPTCHA v3 token. Prospect-flow callers
  // (/signup, /care/get-started step 3) compute it via
  // executeRecaptchaV3('signup_otp_send'); legacy callers (none today)
  // pass nothing and the backend trivially passes captcha verification
  // when no host credential is configured.
  const response = await sendWhatsAppOtpRaw(mobile, {
    allowRegistration: true,
    recaptchaToken: options.recaptchaToken,
  })
  if (response?.status === 200 && response?.data?.success) {
    return {
      sessionId: response.data.sessionId,
      channel: 'whatsapp',
      otpExpirySeconds: response.data.otpExpiry || 300,
      resendDelaySeconds: 20,
    }
  }
  const errorData = response?.response?.data || response?.data || {}
  const err = new Error(errorData.message || 'Could not send WhatsApp code. Please try again.')
  err.code = errorData.errorCode || response?.status
  // The rate-limit response carries a retry-after window so the UI
  // can render a live countdown ("Try again in 22s…"). We pass
  // through both shapes the backend may send:
  //   - retryAfter:        integer seconds until next allowed send
  //   - resendAvailableAt: ISO instant for clock-skew-tolerant clients
  // Both are optional; null/undefined means we don't know.
  if (errorData.retryAfter != null) {
    const n = Number(errorData.retryAfter)
    if (Number.isFinite(n) && n > 0) err.retryAfter = Math.ceil(n)
  }
  if (errorData.resendAvailableAt) {
    err.resendAvailableAt = errorData.resendAvailableAt
  }
  throw err
}

/**
 * Verify the OTP. In real mode this calls the existing
 * `verifyWhatsAppOtp` helper which (with allowRegistration=true) ALSO
 * creates a ROLE_USER session for new mobiles. We persist the returned
 * auth token so the subsequent initialize call is authenticated.
 *
 * Returns `{ verified, mobile, channel, sessionId }`.
 */
export async function verifyProspectOtp({ mobile, otp, sessionId, channel }) {
  if (MOCK_PROSPECT_SIGNUP) {
    await delay(600)
    if (!/^\d{4,6}$/.test(otp || '')) {
      const e = new Error('Please enter the 4–6 digit code we sent.')
      e.code = 'INVALID_OTP'
      throw e
    }
    if (otp === '0000') {
      const e = new Error('That code did not work. Please try again.')
      e.code = 'INVALID_OTP'
      throw e
    }
    return { verified: true, otpToken: fakeId('otpTok'), sessionId, channel, mobile }
  }
  const response = await verifyWhatsAppOtpRaw(mobile, otp, sessionId, { allowRegistration: true })
  if (response?.status === 200 && response?.data?.accessToken) {
    persistAuthSession(response.data)
    return { verified: true, sessionId, channel, mobile }
  }
  const errorData = response?.response?.data || response?.data || {}
  const code = errorData.errorCode
  let message = errorData.message || 'Invalid OTP'
  if (code === 'OTP_EXPIRED') message = 'OTP has expired. Please request a new code.'
  else if (code === 'INVALID_OTP') message = 'Invalid code. Please check and try again.'
  else if (code === 'SESSION_NOT_FOUND') message = 'Session expired. Please request a new code.'
  else if (code === 'MAX_ATTEMPTS_EXCEEDED') message = 'Too many attempts. Please request a new code.'
  const err = new Error(message)
  err.code = code || response?.status || 'OTP_FAILED'
  throw err
}

/**
 * Create the Patient-Prospect record (and add the role + Lead +
 * CareIntakeCase) for the current session. In real mode this assumes
 * `verifyProspectOtp` was called just before and `localStorage.token`
 * is populated.
 */
export async function signupPatientProspect(payload) {
  if (MOCK_PROSPECT_SIGNUP) {
    await delay(900)
    return mockSignup(payload)
  }
  // Real path: the user is already authenticated thanks to verifyProspectOtp
  // (which persisted the token via persistAuthSession). Call the
  // authenticated initialize endpoint.
  if (!isLoggedIn()) {
    throw new Error('Your session expired. Please verify your code again.')
  }
  // Belt-and-suspenders: the orchestrator should never reach this code path
  // without a resolved pathway, but if it does (stale draft, deep-link with
  // an unmatched `?initial=`, etc.) the backend rejects with 4402. Surface a
  // clearer message synchronously so the user can re-pick instead of seeing
  // a generic "we could not start" toast.
  if (typeof payload.pathway !== 'string' || !payload.pathway.trim()) {
    throw new Error(
      'We could not read which kind of care you picked. Please go back and pick a pathway again.',
    )
  }
  const initPayload = {
    name: payload.name || null,
    mobile: payload.mobile,
    email: payload.email || null,
    pathway: payload.pathway,
    specialtyTag: payload.specialtyTag,
    subjectType: payload.subjectType || 'SELF',
    consentGiven: true,
    source: payload.source || 'WEB',
    // Pre-OTP demographics added in the Phase-3 OTP step rework. Backend
    // (PatientProspectSignupRequest) packs these into intakeAnswers via
    // upsertProspectRecord — top-level for SELF, into intakeAnswers.subject
    // for DEPENDENT/CHILD. All three are optional; null/empty values are
    // ignored server-side.
    age: typeof payload.age === 'number' && Number.isFinite(payload.age) ? payload.age : null,
    gender: payload.gender || null,
    subjectName: payload.subjectName || null,
    // Funnel attribution for the support dashboard. Set by the booking
    // entry page (/book-consultation → /care/get-started?intent=consultation),
    // null for the broader /care/start funnel.
    bookingIntent: payload.bookingIntent || null,
  }
  const data = await initializeProspectAuthenticated(initPayload)
  // /initialize ships a fresh JWT alongside the bundle (the OTP-login token
  // the user came in with only carried ROLE_USER; ROLE_PATIENT_PROSPECT was
  // added by this very call, so subsequent
  // hasRole('ROLE_PATIENT_PROSPECT')-gated endpoints — GET /me/patient-prospect,
  // PATCH /intake, POST /reports — would 403 with the old token). Swap the
  // new token into localStorage immediately so the same browser session
  // can fetch the prospect bundle and render /care/status without a manual
  // re-login.
  if (data?.accessToken) {
    persistAuthSession({
      accessToken: data.accessToken,
      accessTokenExpiry: data.accessTokenExpiry,
      authorities: Array.isArray(data.authorities) ? data.authorities : null,
    })
  }
  // The backend returns PatientProspectMeResponse; normalize into the
  // shape the rest of the frontend (CareStatusView) already understands.
  // existingPatientPath===true is the "user already has ROLE_PATIENT,
  // their care request was logged but no prospect role/record was
  // created" case — see PatientProspectService.initializeForLoggedInUser
  // for the rationale. Callers route those users to /patient-home
  // instead of /care/status.
  const prospect = data.prospect || {}
  const recommendation = data.recommendation || null
  const careCase = data.careCase || null
  return {
    userId: data.userId || prospect.userId,
    roles: Array.isArray(data.authorities) ? data.authorities : getStoredAuthorities(),
    prospectInfoId: prospect.id,
    caseId: prospect.caseId || careCase?.id || null,
    name: prospect.name || payload.name || null,
    mobile: prospect.mobile || payload.mobile,
    pathway: prospect.pathway,
    specialtyTag: prospect.specialtyTag,
    subjectType: prospect.subjectType,
    createdAt: prospect.createdAt || new Date().toISOString(),
    recommendation,
    contactOptions: data.contactOptions || null,
    existingPatientPath: data.existingPatientPath === true,
  }
}

/**
 * Fetch the authenticated user's prospect bundle from
 * GET /api/secure/me/patient-prospect. Returns null on 404 / no record.
 */
export async function fetchMyProspectBundle() {
  if (MOCK_PROSPECT_SIGNUP) {
    return getMockBundleFromDraft()
  }
  if (!isLoggedIn()) return null
  const res = await getData(PROSPECT_ME_BASE)
  if (res?.status >= 200 && res?.status < 300) {
    return res.data || null
  }
  return null
}

// ---------------------------------------------------------------------------
// Phase 3 — authenticated post-login intake + reports
// ---------------------------------------------------------------------------

/**
 * Build authenticated-request headers with country/language hints, mirroring
 * the wellness-app pattern. Used by PATCH and multipart endpoints that the
 * existing `postData` helper doesn't cover directly.
 */
function authHeaders(extra = {}) {
  const token = getAuthToken()
  const countryCode = getCountryCode()
  const languageCode = getMainLanguageCode()
  return {
    ...commonHeadersWeb,
    ...(token && { Authorization: `Bearer ${token}` }),
    'ef-user-country-code': countryCode,
    'ef-app-language': languageCode,
    ...extra,
  }
}

/**
 * Mock-mode storage for Phase-3 intake updates / reports so the form is
 * fully exercisable without a backend during local UX iteration.
 */
const MOCK_BUNDLE_KEY = 'mockPatientProspectBundle'

function readMockBundle() {
  try {
    const raw = window.localStorage.getItem(MOCK_BUNDLE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function writeMockBundle(bundle) {
  try {
    window.localStorage.setItem(MOCK_BUNDLE_KEY, JSON.stringify(bundle))
  } catch {
    // ignore
  }
}

function ensureMockBundle() {
  let bundle = readMockBundle()
  if (bundle) return bundle
  const session = getMockProspectSession()
  if (!session) return null
  bundle = {
    prospect: {
      id: session.prospectInfoId,
      userId: session.userId,
      name: session.name,
      mobile: session.mobile,
      pathway: session.pathway,
      specialtyTag: session.specialtyTag,
      subjectType: session.subjectType || 'SELF',
      caseId: session.caseId,
      status: 'SUBMITTED',
      intakeAnswers: {},
      reports: [],
      createdAt: session.createdAt,
    },
    careCase: { id: session.caseId, status: 'INTAKE_IN_PROGRESS' },
    recommendation: session.recommendation || null,
  }
  writeMockBundle(bundle)
  return bundle
}

function getMockBundleFromDraft() {
  // For mock mode the "server bundle" is whatever we've written locally.
  return ensureMockBundle()
}

/**
 * PATCH /api/secure/me/patient-prospect/intake — merges any subset of
 * intake fields. Server returns the refreshed bundle.
 */
export async function updateProspectIntake(payload) {
  if (MOCK_PROSPECT_SIGNUP) {
    const bundle = ensureMockBundle()
    if (!bundle) throw new Error('No active care request to update.')
    const next = { ...(bundle.prospect.intakeAnswers || {}) }
    Object.entries(payload || {}).forEach(([k, v]) => {
      if (v === undefined) return
      if (k === 'subjectType' || k === 'preferredClinicHint') {
        bundle.prospect[k] = v
      } else if (k === 'subject' && v != null) {
        next.subject = v
      } else {
        next[k] = v
      }
    })
    bundle.prospect.intakeAnswers = next
    writeMockBundle(bundle)
    return bundle
  }
  if (!isLoggedIn()) throw new Error('Your session expired. Please sign in again.')
  const url = `${apiURL}${PROSPECT_ME_BASE}/intake?languageCode=${getMainLanguageCode()}&platform=web&countryCode=${getCountryCode()}`
  try {
    const res = await axios.patch(url, payload, { headers: authHeaders({ 'Content-Type': 'application/json' }) })
    if (res?.status >= 200 && res?.status < 300) {
      return res.data || null
    }
    throw new Error(toErrorMessage(res, 'Could not save your changes just now.'))
  } catch (e) {
    throw new Error(toErrorMessage(e, 'Could not save your changes just now.'))
  }
}

/**
 * POST /api/secure/me/patient-prospect/reports — multipart upload of one
 * report. Returns the updated PatientProspectInfo (with the new
 * ReportRef appended).
 */
export async function uploadProspectReport(file, label) {
  if (!file) throw new Error('Please pick a file to upload.')
  if (MOCK_PROSPECT_SIGNUP) {
    const bundle = ensureMockBundle()
    if (!bundle) throw new Error('No active care request to attach reports to.')
    const reports = bundle.prospect.reports || []
    if (reports.length >= 10) throw new Error('Up to 10 reports can be uploaded.')
    reports.push({
      id: `PRR_${Math.random().toString(36).slice(2, 10)}`,
      fileName: file.name,
      contentType: file.type,
      sizeBytes: file.size,
      label: label || null,
      uploadedAt: new Date().toISOString(),
    })
    bundle.prospect.reports = reports
    writeMockBundle(bundle)
    return bundle.prospect
  }
  if (!isLoggedIn()) throw new Error('Your session expired. Please sign in again.')
  const fd = new FormData()
  fd.append('file', file)
  if (label) fd.append('label', label)
  const url = `${apiURL}${PROSPECT_ME_BASE}/reports?languageCode=${getMainLanguageCode()}&platform=web&countryCode=${getCountryCode()}`
  try {
    // Don't set Content-Type — axios + FormData picks the multipart boundary.
    const res = await axios.post(url, fd, { headers: authHeaders() })
    if (res?.status >= 200 && res?.status < 300) {
      return res.data || null
    }
    throw new Error(toErrorMessage(res, 'Could not upload that file just now.'))
  } catch (e) {
    throw new Error(toErrorMessage(e, 'Could not upload that file just now.'))
  }
}

/**
 * DELETE /api/secure/me/patient-prospect/reports/{reportId} — soft remove.
 */
export async function deleteProspectReport(reportId) {
  if (!reportId) return null
  if (MOCK_PROSPECT_SIGNUP) {
    const bundle = ensureMockBundle()
    if (!bundle) return null
    bundle.prospect.reports = (bundle.prospect.reports || []).filter((r) => r.id !== reportId)
    writeMockBundle(bundle)
    return bundle.prospect
  }
  if (!isLoggedIn()) throw new Error('Your session expired. Please sign in again.')
  const url = `${apiURL}${PROSPECT_ME_BASE}/reports/${encodeURIComponent(reportId)}?languageCode=${getMainLanguageCode()}&platform=web&countryCode=${getCountryCode()}`
  try {
    const res = await axios.delete(url, { headers: authHeaders() })
    if (res?.status >= 200 && res?.status < 300) {
      return res.data || null
    }
    throw new Error(toErrorMessage(res, 'Could not remove that report just now.'))
  } catch (e) {
    throw new Error(toErrorMessage(e, 'Could not remove that report just now.'))
  }
}

export function clearMockProspectBundle() {
  if (!MOCK_PROSPECT_SIGNUP) return
  try {
    window.localStorage.removeItem(MOCK_BUNDLE_KEY)
  } catch {
    // ignore
  }
}

/**
 * Phase-1 mock session reader. Returns null in real mode.
 */
export function getMockProspectSession() {
  if (!MOCK_PROSPECT_SIGNUP) return null
  try {
    const raw = window.localStorage.getItem(MOCK_PROSPECT_TOKEN_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export function clearMockProspectSession() {
  if (!MOCK_PROSPECT_SIGNUP) return
  try {
    window.localStorage.removeItem(MOCK_PROSPECT_TOKEN_KEY)
  } catch {
    // ignore
  }
}

// Re-exported for callers that need to know whether the flow is mocked
// (mostly Phase-1 leftovers that show "Phase 1 mock" copy).
export const PROSPECT_MOCK_MODE = MOCK_PROSPECT_SIGNUP

// ---------------------------------------------------------------------------
// PatientAssessmentResult — per-user assessment-results store
// ---------------------------------------------------------------------------

/**
 * POST /api/secure/me/assessment-results — saves one completed
 * assessment against the current user. Caller must be authenticated;
 * silently no-ops (returns null) when there's no token, since this is
 * a best-effort sidecar to the legacy `/api/secure/assessment/{id}/ans`
 * write the SPA already does.
 *
 * `payload` shape mirrors {@code PatientAssessmentResultSaveRequest}:
 *   { assessmentSlug, assessmentId, assessmentName, userScore,
 *     domainScores, severityBucket, recommendedArticleId, answers,
 *     takenAnonymouslyThenSaved, takenAt, durationMillis }
 */
export async function saveAssessmentResult(payload) {
  if (MOCK_PROSPECT_SIGNUP) return payload || null
  if (!isLoggedIn()) return null
  if (!payload) return null
  try {
    const res = await postData(ASSESSMENT_RESULTS_BASE, payload)
    const httpStatus = res?.status ?? res?.response?.status
    const body = res?.data ?? res?.response?.data
    if (httpStatus >= 200 && httpStatus < 300) return body || null
    return null
  } catch (e) {
    // Best-effort sidecar — never let a failure here block the assessment
    // results page from rendering. The legacy store is still the source
    // of truth for the wellness app.
    console.warn('saveAssessmentResult failed (non-fatal):', e?.message || e)
    return null
  }
}

/**
 * Cache the result of a take so a later /signup → /care/status replay
 * can persist it against the authenticated endpoint. Used when the
 * user finishes an assessment without being signed in.
 */
export function stashPendingAssessmentResult(payload) {
  if (!payload) return
  try {
    window.sessionStorage.setItem(
      PENDING_ASSESSMENT_RESULT_KEY,
      JSON.stringify({ payload, stashedAt: Date.now() }),
    )
  } catch {
    // sessionStorage failures are non-fatal — we just lose the handoff.
  }
}

export function readPendingAssessmentResult() {
  try {
    const raw = window.sessionStorage.getItem(PENDING_ASSESSMENT_RESULT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed?.payload || null
  } catch {
    return null
  }
}

export function clearPendingAssessmentResult() {
  try {
    window.sessionStorage.removeItem(PENDING_ASSESSMENT_RESULT_KEY)
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Wellness opt-out — quiet exit for /signup → wellness-only users
// ---------------------------------------------------------------------------

/**
 * POST /api/secure/me/patient-prospect/wellness-opt-out — flips the
 * prospect record to STATUS_WELLNESS_OPT_OUT so support dashboards can
 * filter these out of "active prospects" views. Used by the
 * "Just here for the AUMHUM wellness app?" link on /care/status when
 * a /signup-created prospect realises they actually wanted just the
 * wellness app.
 *
 * Best-effort: the link still navigates to / on failure so the user
 * isn't trapped on /care/status if the call hiccups.
 */
export async function optOutOfWellnessFunnel() {
  if (MOCK_PROSPECT_SIGNUP) {
    const bundle = ensureMockBundle()
    if (bundle?.prospect) {
      bundle.prospect.status = 'WELLNESS_OPT_OUT'
      writeMockBundle(bundle)
    }
    return bundle || null
  }
  if (!isLoggedIn()) return null
  try {
    const res = await postData(PROSPECT_ME_BASE + '/wellness-opt-out', {})
    const httpStatus = res?.status ?? res?.response?.status
    const body = res?.data ?? res?.response?.data
    if (httpStatus >= 200 && httpStatus < 300) return body || null
    return null
  } catch (e) {
    console.warn('optOutOfWellnessFunnel failed (non-fatal):', e?.message || e)
    return null
  }
}
